/**
 * Secure Token Storage Utility
 * Stores access tokens in localStorage for persistence across page refreshes
 * Refresh tokens are handled via httpOnly cookies
 */

class SecureTokenStorage {
  constructor() {
    this.TOKEN_KEY = 'accessToken';
    this.EXPIRY_KEY = 'tokenExpiry';
  }

  /**
   * Store access token in localStorage
   */
  setAccessToken(token) {
    // Store token in localStorage
    localStorage.setItem(this.TOKEN_KEY, token);
    
    // Calculate and store token expiry time
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const tokenExpiry = payload.exp * 1000; // Convert to milliseconds
      localStorage.setItem(this.EXPIRY_KEY, tokenExpiry.toString());
    } catch (error) {
      localStorage.removeItem(this.EXPIRY_KEY);
    }
  }

  /**
   * Get access token from localStorage
   */
  getAccessToken() {
    const token = localStorage.getItem(this.TOKEN_KEY);
    const expiry = localStorage.getItem(this.EXPIRY_KEY);
    
    // Check if token is expired
    if (token && expiry && Date.now() >= parseInt(expiry)) {
      this.clearAccessToken();
      return null;
    }
    
    return token;
  }

  /**
   * Clear access token from localStorage
   */
  clearAccessToken() {
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.EXPIRY_KEY);
  }

  /**
   * Check if access token is valid (not expired)
   */
  isTokenValid() {
    const token = this.getAccessToken();
    return token !== null;
  }

  /**
   * Get token expiry time
   */
  getTokenExpiry() {
    const expiry = localStorage.getItem(this.EXPIRY_KEY);
    return expiry ? parseInt(expiry) : null;
  }

  /**
   * Check if token will expire soon (within 10 minutes for short tokens, 1 hour for long tokens)
   * Increased buffer to reduce frequent refresh attempts
   */
  isTokenExpiringSoon() {
    const expiry = this.getTokenExpiry();
    if (!expiry) return false;
    
    // Calculate token lifetime to determine if it's a "remember me" token
    const token = this.getAccessToken();
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const tokenCreatedAt = payload.iat * 1000;
        const tokenLifetime = expiry - tokenCreatedAt;
        
        // If token lifetime is more than 12 hours, it's a "remember me" token
        // Use 1 hour buffer for long tokens, 10 minutes for short tokens (increased from 5 min)
        const buffer = tokenLifetime > (12 * 60 * 60 * 1000) 
          ? 60 * 60 * 1000 // 1 hour for remember me tokens
          : 10 * 60 * 1000; // 10 minutes for regular tokens (increased to reduce refresh frequency)
        
        return (expiry - Date.now()) <= buffer;
      } catch (error) {
        // Fallback to 10 minutes if parsing fails
        const tenMinutes = 10 * 60 * 1000;
        return (expiry - Date.now()) <= tenMinutes;
      }
    }
    
    // Default to 10 minutes if no token
    const tenMinutes = 10 * 60 * 1000;
    return (expiry - Date.now()) <= tenMinutes;
  }
}

// Create singleton instance
const tokenStorage = new SecureTokenStorage();

export default tokenStorage;

